"use client";

import { useEffect, useRef, useState } from "react";
import { useAuth } from "@workos-inc/authkit-nextjs/components";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useConvexAuth, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { useDelayElapsed } from "@/hooks/use-delay-elapsed";
import { buildAuthRecoveryHref } from "@/lib/auth-recovery";
import { formatCents } from "@/lib/formatting/currency";
import { cn } from "@/lib/utils";
import { useListingQuery, type ListingQuery } from "./listing-query";
import {
  useListingQuoteSummary,
  type ListingQuoteSummary,
} from "./listing-quote-summary";

interface StickyMobileCTAProps {
  href: string;
  label: string;
  priceCents: number;
  unitName: string;
  productId: Id<"products">;
}

const AUTH_RECOVERY_DELAY_MS = 2_000;

export function StickyMobileCTA({
  href,
  label,
  priceCents,
  unitName,
  productId,
}: StickyMobileCTAProps) {
  const { visible, sentinelRef } = useStickyVisibility();
  const { user, loading } = useAuth();
  const convexAuth = useConvexAuth();
  const pathname = usePathname();
  const searchParams = useListingQuery();
  const isAuthenticated = Boolean(user && convexAuth.isAuthenticated);
  const listingRelationship = useQuery(
    api.products.ownershipQueries.getMyListingRelationship,
    isAuthenticated ? { productId } : "skip",
  );
  const isRelationshipReady =
    !loading &&
    !convexAuth.isLoading &&
    (!user || (isAuthenticated && listingRelationship !== undefined));

  return (
    <>
      <div ref={sentinelRef} className="h-0 w-0" aria-hidden />
      {user && !loading && !convexAuth.isLoading && !isAuthenticated ? (
        <StickyAuthRecovery
          returnTo={buildReturnTo(pathname, searchParams)}
          visible={visible}
        />
      ) : isRelationshipReady ? (
        <StickyMobileAction
          href={href}
          label={label}
          ownsListing={listingRelationship?.ownsListing === true}
          priceCents={priceCents}
          unitName={unitName}
          productId={productId}
          visible={visible}
        />
      ) : null}
    </>
  );
}

function StickyAuthRecovery({
  returnTo,
  visible,
}: {
  returnTo: string;
  visible: boolean;
}) {
  const didGracePeriodElapse = useDelayElapsed(AUTH_RECOVERY_DELAY_MS);
  if (!didGracePeriodElapse) return null;

  return (
    <StickyMobileBar visible={visible}>
      <span className="text-sm font-semibold">Session needs attention</span>
      <Button variant="primary" size="default" asChild>
        <a href={buildAuthRecoveryHref(returnTo)}>Reconnect</a>
      </Button>
    </StickyMobileBar>
  );
}

function useStickyVisibility() {
  const [visible, setVisible] = useState(false);
  const sentinelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return { visible, sentinelRef };
}

function StickyMobileAction({
  href,
  label,
  ownsListing,
  priceCents,
  unitName,
  productId,
  visible,
}: StickyMobileCTAProps & { ownsListing: boolean; visible: boolean }) {
  const { summary, canBuy, actionLabel, buy } = useListingQuoteSummary();
  const actionHref = ownsListing
    ? `/dashboard/inventory/${productId}/edit`
    : href;
  return (
    <StickyMobileBar visible={visible}>
      <StickyMobileValue
        ownsListing={ownsListing}
        priceCents={priceCents}
        unitName={unitName}
        summary={summary}
      />
      {!ownsListing && canBuy ? (
        <Button type="button" variant="primary" onClick={buy}>
          {actionLabel}
        </Button>
      ) : (
        <StickyMobileLink
          href={actionHref}
          label={ownsListing ? "Edit listing" : label}
        />
      )}
    </StickyMobileBar>
  );
}

function StickyMobileLink({ href, label }: { href: string; label: string }) {
  return (
    <Button variant="primary" size="default" asChild>
      <Link href={href}>{label}</Link>
    </Button>
  );
}

function StickyMobileBar({
  children,
  visible,
}: {
  children: React.ReactNode;
  visible: boolean;
}) {
  return (
    <div
      className={cn(
        "fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-sm px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] lg:hidden",
        "transition-transform duration-200 ease-out",
        visible ? "translate-y-0" : "translate-y-full",
      )}
    >
      <div className="mx-auto flex max-w-lg items-center justify-between gap-3">
        {children}
      </div>
    </div>
  );
}

function buildReturnTo(pathname: string, searchParams: ListingQuery) {
  const query = searchParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function StickyMobileValue({
  ownsListing,
  priceCents,
  unitName,
  summary,
}: {
  ownsListing: boolean;
  priceCents: number;
  unitName: string;
  summary: ListingQuoteSummary | null;
}) {
  if (ownsListing) {
    return <span className="text-sm font-semibold">This is your listing</span>;
  }
  if (priceCents === 0) {
    return <span className="text-lg font-bold text-success-strong">Free</span>;
  }
  if (summary) {
    return (
      <div className="flex min-w-0 flex-col">
        <span className="text-lg font-bold tabular-nums">
          {formatCents(summary.totalCents)}
        </span>
        <span className="text-xs text-muted-foreground">
          Delivered, plus tax
        </span>
      </div>
    );
  }
  return (
    <div className="flex min-w-0 items-baseline gap-1">
      <span className="text-lg font-bold tabular-nums">
        {formatCents(priceCents)}
      </span>
      <span className="text-xs text-muted-foreground">/ {unitName}</span>
    </div>
  );
}
